import { useEffect, useRef, useState } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { LoadingStatus } from '@/shared/ui/spinner';

import { downloadFieldReport, useFieldAppeal, useFieldReports } from '../api/field-admin-client';
import { fieldErrorMessage } from '../field-view';

import { FieldStatus } from './field-primitives';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import type { FieldSnapshot } from '@mio/shared';

export function FieldReports({ snapshot }: { snapshot?: FieldSnapshot } = {}) {
  const query = useFieldReports();
  const appeal = useFieldAppeal();
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const reasonInput = useRef<HTMLTextAreaElement>(null);
  const appealDrafts = useRef<Record<string, { reason: string; evidence: string }>>({});
  const [downloading, setDownloading] = useState<string | null>(null);
  const pending = useRef<{ key: string; requestId: string } | null>(null);
  const selectedReport = query.data?.reports.find((entry) => entry.digest === selected)?.report;
  const selectedEvidence = evidence
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter(Boolean);
  const evidenceOptions = [
    ...(snapshot?.discovery?.decisions.map((item) => ({
      id: item.id,
      label: `Decision · ${item.title} · version ${item.version}`,
    })) ?? []),
    ...(snapshot?.discovery?.findings.map((item) => ({
      id: item.id,
      label: `Finding · ${item.title}`,
    })) ?? []),
    ...(snapshot?.prs.map((item) => ({ id: item.id, label: `Pull request · ${item.title}` })) ??
      []),
    ...(snapshot?.documents.map((item) => ({
      id: item.id,
      label: `Document · ${item.title} · version ${item.version}`,
    })) ?? []),
    ...(snapshot?.tickets.map((item) => ({ id: item.id, label: `Ticket · ${item.title}` })) ?? []),
    ...(snapshot?.runs.map((item) => ({
      id: item.id,
      label: `${item.suite.charAt(0).toUpperCase() + item.suite.slice(1)} run · ${item.revision.slice(0, 8)}`,
    })) ?? []),
  ];
  useEffect(() => {
    if (selected) reasonInput.current?.focus();
  }, [selected]);
  useEffect(() => {
    if (selected) appealDrafts.current[selected] = { reason, evidence };
  }, [selected, reason, evidence]);
  return (
    <section className="min-w-0 space-y-4 [overflow-wrap:anywhere]">
      <h3 className="text-sm font-medium">Instructor reports and appeals</h3>
      <p className="text-fg-muted text-sm leading-6">
        {snapshot?.discovery ? 'Progress reviews' : 'Day-five formative feedback'} and final
        readiness remain separate. Assistance is recorded as context. An appeal preserves the
        original report and adds a reviewed outcome.
      </p>
      {query.error ? (
        <Alert>
          <p>{fieldErrorMessage(query.error)}</p>
          <Button
            size="sm"
            variant="secondary"
            className="mt-3"
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Retry reports
          </Button>
        </Alert>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {success ? (
        <p role="status" className="text-sm">
          Appeal submitted. Your original report remains available below.
        </p>
      ) : null}
      {query.isPending ? <LoadingStatus label="Loading reports…" /> : null}
      {query.data?.reports.length === 0 ? (
        <p className="text-fg-muted text-sm">No instructor report has been published.</p>
      ) : null}
      {query.data?.reports.map(({ digest, report }) => (
        <article
          key={digest}
          aria-labelledby={`report-${digest}`}
          className="border-line min-w-0 space-y-3 border-t py-5"
        >
          <div className="flex flex-wrap justify-between gap-3">
            <h4 id={`report-${digest}`} className="text-base font-semibold">
              {snapshot?.discovery
                ? report.checkpointDay === 5
                  ? 'Progress review'
                  : 'Readiness review'
                : `Day ${report.checkpointDay ?? 10}`}{' '}
              · {report.weightedScore}/100 ·{' '}
              {(report.checkpointDay ?? 10) === 5
                ? 'Formative'
                : report.passed
                  ? 'Ready'
                  : 'Further work required'}
            </h4>
            <Button
              size="sm"
              variant="secondary"
              loading={downloading === digest}
              disabled={downloading !== null}
              onClick={() => {
                setDownloading(digest);
                setError(null);
                void downloadFieldReport(digest)
                  .catch((cause) => setError(fieldErrorMessage(cause)))
                  .finally(() => setDownloading(null));
              }}
            >
              Download report
            </Button>
          </div>
          <p className="text-fg-muted flex flex-wrap gap-x-3 gap-y-1 text-xs">
            <span>
              Revision{' '}
              <code title={report.candidateRevision}>{report.candidateRevision.slice(0, 8)}</code>
            </span>
            <time dateTime={report.moderatedAt}>
              {new Date(report.moderatedAt).toLocaleString()}
            </time>
          </p>
          <p className="whitespace-pre-wrap text-sm leading-6">{report.feedback}</p>
          <p className="text-fg-muted whitespace-pre-wrap text-xs">
            Assistance: {report.assistance || 'No additional assistance recorded.'}
          </p>
          <details>
            <summary className="cursor-pointer text-sm font-medium">
              Dimension ratings and evidence
            </summary>
            <ul className="mt-3 space-y-3">
              {report.ratings.map((r) => (
                <li key={r.dimension} className="text-sm">
                  <strong>
                    {(
                      r.dimension.charAt(0).toUpperCase() +
                      r.dimension.slice(1).replaceAll('_', ' ')
                    ).replace(/^Ai /, 'AI ')}{' '}
                    · {r.rating}/4
                  </strong>
                  <p>{r.rationale}</p>
                  <p className="text-fg-muted break-all text-xs">{r.evidenceIds.join(', ')}</p>
                </li>
              ))}
            </ul>
          </details>
          {query.data?.appeals.some((a) => a.reportDigest === digest) ? null : (
            <Button
              size="sm"
              variant="secondary"
              aria-expanded={selected === digest}
              disabled={appeal.isPending}
              onClick={() => {
                setReason(appealDrafts.current[digest]?.reason ?? '');
                setEvidence(appealDrafts.current[digest]?.evidence ?? '');
                setSelected(digest);
                setSuccess(false);
                setError(null);
              }}
            >
              Appeal this report
            </Button>
          )}
          {selected === digest ? (
            <form
              className="border-line mt-4 min-w-0 space-y-3 border-t pt-4"
              aria-label={
                snapshot?.discovery
                  ? `Appeal ${selectedReport?.checkpointDay === 5 ? 'progress' : 'readiness'} review`
                  : `Appeal day ${selectedReport?.checkpointDay ?? 10} report`
              }
              onSubmit={(event) => {
                event.preventDefault();
                if (appeal.isPending) return;
                const input = {
                  reportDigest: selected,
                  reason,
                  evidenceIds: evidence
                    .split(/[\n,]/)
                    .map((v) => v.trim())
                    .filter(Boolean),
                };
                const key = JSON.stringify(input);
                const requestId =
                  pending.current?.key === key ? pending.current.requestId : crypto.randomUUID();
                pending.current = { key, requestId };
                setError(null);
                void appeal
                  .mutateAsync({ ...input, requestId })
                  .then(() => {
                    pending.current = null;
                    delete appealDrafts.current[selected];
                    setSuccess(true);
                    setSelected('');
                    setReason('');
                    setEvidence('');
                  })
                  .catch((cause) => setError(fieldErrorMessage(cause)));
              }}
            >
              <h4 className="text-sm font-semibold">
                {snapshot?.discovery
                  ? `Appeal ${selectedReport?.checkpointDay === 5 ? 'progress' : 'readiness'} review`
                  : `Appeal day ${selectedReport?.checkpointDay ?? 10} report`}
              </h4>
              <p className="text-fg-muted text-sm leading-6">
                Explain what should change and attach the work that supports your request. The
                instructor will review it alongside this report.
              </p>
              <label className={FIELD_LABEL} htmlFor="appeal-reason">
                What should be reconsidered, and why?
              </label>
              <textarea
                ref={reasonInput}
                id="appeal-reason"
                aria-describedby="appeal-reason-help"
                className={FIELD_INPUT}
                minLength={40}
                maxLength={8000}
                required
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p id="appeal-reason-help" className="text-fg-muted text-xs">
                At least 40 characters. Be specific about the decision and evidence.
              </p>
              {evidenceOptions.length ? (
                <fieldset className="space-y-2">
                  <legend className={FIELD_LABEL}>Supporting evidence</legend>
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {evidenceOptions.map((item) => (
                      <label
                        key={item.id}
                        className="hover:bg-surface flex items-start gap-2 py-1.5 text-sm leading-5"
                      >
                        <input
                          type="checkbox"
                          className="accent-primary mt-1 shrink-0"
                          checked={selectedEvidence.includes(item.id)}
                          disabled={
                            appeal.isPending ||
                            (selectedEvidence.length >= 20 && !selectedEvidence.includes(item.id))
                          }
                          onChange={(event) =>
                            setEvidence(
                              (event.target.checked
                                ? [...selectedEvidence, item.id]
                                : selectedEvidence.filter((id) => id !== item.id)
                              ).join(', '),
                            )
                          }
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : null}
              <details open={!evidenceOptions.length} className="text-sm">
                <summary className="text-fg-muted cursor-pointer">Enter other evidence IDs</summary>
                <div className="mt-3 space-y-2">
                  <label className={FIELD_LABEL} htmlFor="appeal-evidence">
                    Existing evidence IDs, separated by commas or new lines
                  </label>
                  <textarea
                    id="appeal-evidence"
                    className={FIELD_INPUT}
                    required
                    value={evidence}
                    onChange={(e) => setEvidence(e.target.value)}
                  />
                </div>
              </details>
              <p className="text-fg-muted text-xs">
                {selectedEvidence.length} reference{selectedEvidence.length === 1 ? '' : 's'}{' '}
                selected · maximum 20
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  loading={appeal.isPending}
                  disabled={
                    reason.trim().length < 40 || !evidence.trim() || selectedEvidence.length > 20
                  }
                >
                  Submit appeal
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={appeal.isPending}
                  onClick={() => {
                    setSelected('');
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
        </article>
      ))}

      {query.data?.appeals.map((a) => (
        <article key={a.id} className="border-line min-w-0 space-y-2 border-t py-4">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold">Appeal</h4>
            <FieldStatus value={a.status} />
          </div>
          <p className="whitespace-pre-wrap text-sm">{a.reason}</p>
          {a.response ? (
            <p className="whitespace-pre-wrap text-sm">Instructor response: {a.response}</p>
          ) : null}
        </article>
      ))}
    </section>
  );
}
